
import { useEffect, useRef } from "react";
import { Dealer } from "@/types/dealer";
import { Coordinates } from "../types";
import { refreshDealersData } from "../refreshData";
import { debounce } from "@/lib/utils/debounce";

/**
 * Hook to handle coordinate changes for dealer data
 */
export function useCoordinatesEffect(
  coordinates: Coordinates | null | undefined,
  selectedMetal: string,
  initialLoadDone: boolean,
  freshDataLoading: { value: boolean },
  fetchController: React.RefObject<AbortController | null>,
  setDealers: (dealers: Dealer[]) => void
) {
  const lastCoordinates = useRef<string | null>(null);
  const fetchCount = useRef(0);
  const lastFetchTime = useRef(0);
  
  // Debounced fetch function to prevent excessive calls
  const debouncedFetch = useRef(
    debounce(async (coords: Coordinates, metal: string) => {
      const now = Date.now();
      
      // Anti-loop protection: max 3 fetches per 10 seconds
      if (now - lastFetchTime.current < 10000) {
        fetchCount.current++;
        if (fetchCount.current > 3) {
          console.log("🚫 useCoordinatesEffect: Too many fetches, skipping");
          return;
        }
      } else {
        fetchCount.current = 1;
      }
      
      lastFetchTime.current = now;
      
      console.log("🔄 useCoordinatesEffect: Debounced fetch triggered", {
        coords,
        metal,
        fetchCount: fetchCount.current
      });
      
      // Set loading state to avoid multiple simultaneous requests
      freshDataLoading.value = true;
      
      try {
        await refreshDealersData(
          metal,
          coords,
          fetchController,
          () => {}, // We don't want to set loading state here
          () => {}, // We don't want to set error state here
          setDealers
        );
      } finally {
        // Reset loading state
        freshDataLoading.value = false;
      }
    }, 1000) // 1 second debounce
  );

  useEffect(() => {
    // Only proceed if we have valid coordinates and initial load is done
    if (!coordinates || !initialLoadDone) {
      return;
    }
    
    // Don't refetch if we're already fetching
    if (freshDataLoading.value) {
      console.log("⏳ useCoordinatesEffect: Already fetching, skipping");
      return;
    }
    
    // Create coordinate string for comparison
    const coordString = `${coordinates.latitude.toFixed(4)},${coordinates.longitude.toFixed(4)}`;
    
    // Only fetch if coordinates actually changed
    if (lastCoordinates.current === coordString) {
      console.log("📍 useCoordinatesEffect: Coordinates unchanged, skipping");
      return;
    }
    
    console.log("📍 useCoordinatesEffect: Coordinates changed", {
      from: lastCoordinates.current,
      to: coordString,
      metal: selectedMetal
    });
    
    lastCoordinates.current = coordString;
    
    // Use debounced fetch
    debouncedFetch.current(coordinates, selectedMetal);
    
    // Cleanup function
    return () => {
      // Don't reset lastCoordinates on cleanup to maintain state
    };
  }, [coordinates?.latitude, coordinates?.longitude, selectedMetal, initialLoadDone]);
  
  // Reset fetch count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCount.current = 0;
    }, 60000); // Reset every minute
    
    return () => clearInterval(interval);
  }, []);
}
